import type { AccountStatus, AuthUser, StatusFields } from '@/types/auth';

const OPTIONAL_STATUS_KEYS = [
  'test_started_at',
  'test_ends_at',
  'test_passed_at',
  'whatsapp_link_sent_at',
  'video_watched_at',
  'rules_acknowledged_at',
  'verification_video_watched_at',
  'pre_banned_at',
  'banned_at',
  'streak_resumed_at',
  'redemption_started_at',
  'redemption_ends_at',
  'redemption_passed_at',
  'usdc_wallet',
  'phone',
  'country',
  'city',
  'birthdate',
  'vacation_starts_at',
  'vacation_ends_at',
] as const;

const OPTIONAL_BASE_KEYS = [
  'verification_photo',
  'personal_phrase',
  'timezone',
  'member_color',
] as const;

function pickNonEmptyStrings<K extends string>(
  record: Record<string, unknown>,
  keys: readonly K[]
): Partial<Record<K, string>> {
  const out: Partial<Record<K, string>> = {};
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') out[key] = value;
  }
  return out;
}

function mapBaseFields(
  record: Record<string, unknown>
): Omit<AuthUser, keyof StatusFields | 'personal_ig_username'> {
  // verification_photo + personal_phrase are optional on AuthUser, which
  // makes TypeScript permissive about omitting them — but the verification
  // screen needs verification_photo to build the locked-preview URL.
  return {
    id: record['id'] as string,
    collectionId: (record['collectionId'] as string) ?? '',
    email: record['email'] as string,
    ig_username: (record['ig_username'] as string) ?? '',
    first_name: (record['first_name'] as string) ?? '',
    last_name: (record['last_name'] as string) ?? '',
    role: (record['role'] as AuthUser['role']) ?? 'collector',
    is_active: (record['is_active'] as boolean) ?? true,
    photo: (record['photo'] as string) ?? '',
    created: (record['created'] as string) ?? '',
    player_number: (record['player_number'] as number) ?? 0,
    ...pickNonEmptyStrings(record, OPTIONAL_BASE_KEYS),
  };
}

function mapStatusFields(record: Record<string, unknown>): StatusFields {
  return {
    account_status: (record['account_status'] as AccountStatus) ?? 'healthy',
    top_tier: (record['top_tier'] as boolean) ?? false,
    wallet_warning_dismissed:
      (record['wallet_warning_dismissed'] as boolean) ?? false,
    must_reset_password: (record['must_reset_password'] as boolean) ?? false,
    has_used_redemption: (record['has_used_redemption'] as boolean) ?? false,
    ...pickNonEmptyStrings(record, OPTIONAL_STATUS_KEYS),
  };
}

export function mapRecordToUser(record: Record<string, unknown>): AuthUser {
  const personalIg = (record['personal_ig_username'] as string) || '';
  return {
    ...mapBaseFields(record),
    ...(personalIg ? { personal_ig_username: personalIg } : {}),
    ...mapStatusFields(record),
  };
}
